/* eslint-disable @typescript-eslint/naming-convention */
import classNames from 'classnames';
import React, { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { IModalActionsProps, Actions } from './Actions';
import { IModalContentProps, Content } from './Content';
import { IModalStatusProps, ModalStatus as Status } from './Status';
import { IModalTitleProps, Title } from './Title';

export interface IModalProps {
    id: string;
    children?: any;
    onClose?: (evt: React.SyntheticEvent) => void;
    closeOnBackdropClick?: boolean;
    classNames?: string;
}

interface IModalFunctionComponent extends React.FunctionComponent<IModalProps> {
    Title: React.FunctionComponent<IModalTitleProps>;
    Content: React.FunctionComponent<IModalContentProps>;
    Actions: React.FunctionComponent<IModalActionsProps>;
    Status: React.FunctionComponent<IModalStatusProps>;
}

const Modal: IModalFunctionComponent = (props: IModalProps) => {
    const { onClose, id, closeOnBackdropClick = true } = props;
    const el = document.getElementById('modal-portal');

    const handleClose = (evt: React.SyntheticEvent) => {
        if (onClose) {
            onClose(evt);
        }
    };

    // Pass id prop on to sub-components + hook up callbacks
    let hasActions = false;
    let childrenWithProps: ReactNode | ReactNode[] | null = null;
    if (props.children) {
        childrenWithProps = React.Children.map(props.children, (child) => {
            if (child.type === (<Modal.Title />).type) {
                return React.cloneElement(child, { id, onClose });
            }
            if (child.type === (<Modal.Status />).type) {
                hasActions = true;
            }
            if (child.type === (<Modal.Actions />).type) {
                hasActions = true;
            }
            return React.cloneElement(child, { id });
        });
    }

    React.useEffect(() => {
        if (!document.body || !document.body.parentElement) {
            return undefined;
        }

        document.body.parentElement.classList.add('is-modal-open');

        return () => {
            if (!document.body || !document.body.parentElement) {
                return;
            }

            document.body.parentElement.classList.remove('is-modal-open');
        };
    }, []);

    const classes = classNames('modal', props.classNames, { 'has-actions': hasActions });

    const renderModal = () => (
        <>
            <div
                role="dialog"
                className={classes}
                aria-labelledby={`modal-title-${id}`}
                aria-describedby={`modal-content-${id}`}
                data-role="modal"
                data-type="popup"
                tabIndex={0}
                onClick={(evt: React.SyntheticEvent) => evt.stopPropagation()}
            >
                <div className="modal-inner">{childrenWithProps}</div>
            </div>
            <div
                className="modal-backdrop"
                onClick={(evt: React.SyntheticEvent) => {
                    if (closeOnBackdropClick) {
                        handleClose(evt);
                    } else {
                        evt.stopPropagation();
                    }
                }}
            />
        </>
    );

    if (!el) {
        return null;
    }

    return ReactDOM.createPortal(renderModal(), el);
};

Modal.Title = Title;
Modal.Content = Content;
Modal.Actions = Actions;
Modal.Status = Status;
Modal.displayName = 'Modal';

export { Modal };
